import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type PayRunStatus, formatDate, payslipListResponseSchema } from '@thrivehrm/shared';
import { Download } from 'lucide-react';
import type * as React from 'react';
import { MoneyValue, RevealToggle } from '@/components/ui/reveal-toggle';
import { StatTile } from '@/components/ui/stat-tile';
import { Stepper, type StepperStep } from '@/components/ui/stepper';
import { RowsSkeleton, StatTilesSkeleton, WidgetError } from '@/features/dashboard/widget-states';
import { API_BASE, api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { useSalaryReveal } from '@/lib/reveal';
import { payslipTitle } from './payslips/payslip-title';
import { usePayRuns } from './runs/api';
import { RUN_TYPE_LABELS } from './runs/labels';
import { RunStatusBadge } from './runs/run-status-badge';

const Muted = ({ children }: { children: React.ReactNode }) => (
  <p className="text-small text-muted-foreground">{children}</p>
);

/** The run workflow (FR-PAY-12) as stepper steps; a disbursed run has every step done. */
const RUN_STEPS: readonly { status: PayRunStatus; label: string }[] = [
  { status: 'DRAFT', label: 'Draft' },
  { status: 'SUBMITTED', label: 'Submitted for approval' },
  { status: 'APPROVED', label: 'Approved' },
  { status: 'DISBURSED', label: 'Disbursed' },
];

function runSteps(status: PayRunStatus): StepperStep[] {
  const current = RUN_STEPS.findIndex((s) => s.status === status);
  return RUN_STEPS.map((s, i) => ({
    id: s.status,
    label: s.label,
    state: i < current || status === 'DISBURSED' ? 'done' : i === current ? 'current' : 'upcoming',
  }));
}

/**
 * Payroll runs at a glance (T-06F, EXT-10): how many runs wait for approval and for
 * disbursement, and the latest runs with their status. Reads `GET /payroll/runs`, so each
 * user sees only the runs they may read (AC-8: Finance only approved / disbursed ones). The
 * newest run's progress is a Stepper. No amounts are shown.
 */
export function PayrollRunsWidget() {
  const latest = usePayRuns({ page: 1, pageSize: 5 });
  const submitted = usePayRuns({ page: 1, pageSize: 1, status: 'SUBMITTED' });
  const approved = usePayRuns({ page: 1, pageSize: 1, status: 'APPROVED' });
  if (latest.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <StatTilesSkeleton count={2} />
        <RowsSkeleton count={2} />
      </div>
    );
  }
  if (latest.error) {
    return (
      <WidgetError
        onRetry={() => {
          void latest.refetch();
          void submitted.refetch();
          void approved.refetch();
        }}
        retrying={latest.isRefetching}
      />
    );
  }
  const runs = latest.data.items.filter((r) => r.status !== 'CANCELLED');
  const newest = runs[0];
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2">
        <StatTile label="Awaiting approval" value={submitted.data?.total ?? 0} />
        <StatTile label="Ready to disburse" value={approved.data?.total ?? 0} />
      </div>
      {newest ? (
        <div className="flex flex-col gap-1">
          <p className="text-body-strong text-foreground">{newest.name}</p>
          <Stepper aria-label={`${newest.name} progress`} steps={runSteps(newest.status)} />
        </div>
      ) : null}
      {runs.length === 0 ? (
        <Muted>No payroll runs yet.</Muted>
      ) : (
        <ul aria-label="Latest payroll runs" className="flex flex-col divide-y border-t">
          {runs.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2 py-2">
              <Link
                to="/payroll/runs/$runId"
                params={{ runId: r.id }}
                className="min-w-0 flex-1 basis-40 font-semibold text-primary hover:underline"
              >
                {r.name}
              </Link>
              <span className="text-small text-muted-foreground">
                {RUN_TYPE_LABELS[r.type]} · {formatDate(r.payDate)}
              </span>
              <RunStatusBadge status={r.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The caller's latest payslip (FR-PAY-18, T-06F): heading, pay date, net masked until
 * revealed, the PDF and a link to all payslips. Approved / disbursed runs only (the API's
 * rule); accounts without an employee record have none.
 */
export function LatestPayslipWidget() {
  const [revealed, setRevealed] = useSalaryReveal();
  const params = { page: 1, pageSize: 1 };
  const { data, isPending, error, refetch, isRefetching } = useQuery({
    queryKey: queryKeys.payroll.myPayslips(params),
    queryFn: ({ signal }) =>
      api.get('/me/payslips', { query: params, schema: payslipListResponseSchema, signal }),
  });
  if (isPending) return <StatTilesSkeleton count={1} className="grid-cols-1" />;
  if (error) return <WidgetError onRetry={() => void refetch()} retrying={isRefetching} />;
  const p = data.items[0];
  if (!p) return <Muted>No payslips yet. They appear once a payroll run is approved.</Muted>;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-item text-foreground">{payslipTitle(p)}</p>
          <Muted>Paid {formatDate(p.payDate)}</Muted>
        </div>
        <RevealToggle revealed={revealed} onRevealedChange={setRevealed} />
      </div>
      <StatTile label="Net pay" value={<MoneyValue amount={p.net} revealed={revealed} />} />
      <div className="flex flex-wrap items-center gap-4">
        <a
          href={`${API_BASE}/payslips/${p.lineId}.pdf`}
          download
          className="inline-flex h-10 items-center gap-2 text-small font-semibold text-primary hover:underline"
        >
          <Download className="size-4" aria-hidden="true" />
          Download PDF
        </a>
        <Link
          to="/me"
          search={{ tab: 'payslips' }}
          className="inline-flex h-10 items-center text-small font-semibold text-primary hover:underline"
        >
          All payslips
        </Link>
      </div>
    </div>
  );
}
